import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import type { Chart as ChartJS, ScriptableContext } from 'chart.js';
import type { TreemapDataPoint } from 'chartjs-chart-treemap';
import KpiChartPanel from './KpiChartPanel';
import MsrChartImageDownloadMenu from '../MsrChartImageDownloadMenu';
import { KPI_ORANGE, KPI_PRIMARY_BLUE, KPI_SLATE } from './kpiChartPalette';

/** MSR template treemap palette — blue, orange, grey (matches presentation slides). */
const MSR_TREEMAP_COLORS = [KPI_PRIMARY_BLUE, KPI_ORANGE, KPI_SLATE, '#eab308', '#5b8fce', '#94a3b8'];

const CHART_HEIGHT_PX = 320;

type TreemapLeafAggregate = {
  treemapKey?: string;
  ciLabel?: string;
  value?: number;
  label?: string;
  children?: Array<{ treemapKey?: string; ciLabel?: string; value?: number }>;
};

type TreemapLeafRaw = TreemapDataPoint & {
  _data?: TreemapLeafAggregate;
};

function ciLabelFromLeafRaw(raw: TreemapLeafRaw): string {
  const d = raw._data;
  if (!d) return '';
  if (d.ciLabel?.trim()) return d.ciLabel.trim();
  const fromChild = d.children?.find((c) => c.ciLabel?.trim())?.ciLabel?.trim();
  if (fromChild) return fromChild;
  if (typeof d.label === 'string' && d.label.trim()) return d.label.trim();
  if (typeof raw.g === 'string' && raw.g.trim()) return raw.g.trim();
  return '';
}

export interface MsrCiTreemapChartProps {
  title: string;
  rows: Array<{ label: string; count: number }>;
  emptyMessage?: string;
  /** MSR capture wrapper id — enables per-chart image download in the title row. */
  imageDownloadElementId?: string;
  imageDownloadDisabled?: boolean;
}

const MsrCiTreemapChart: React.FC<MsrCiTreemapChartProps> = ({
  title,
  rows,
  emptyMessage = 'No configuration items linked to tickets for this customer.',
  imageDownloadElementId,
  imageDownloadDisabled = false,
}) => {
  const chartRef = useRef<ChartJS | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const sorted = useMemo(
    () => [...rows].filter((r) => r.count > 0).sort((a, b) => b.count - a.count),
    [rows]
  );

  const totalCiCount = sorted.length;
  const rowsSignature = useMemo(
    () => sorted.map((r) => `${r.label}:${r.count}`).join('|'),
    [sorted]
  );

  const [topN, setTopN] = useState(1);

  useEffect(() => {
    if (totalCiCount > 0) {
      setTopN(totalCiCount);
    }
  }, [rowsSignature, totalCiCount]);

  const displayedRows = useMemo(() => {
    if (!totalCiCount) return [];
    const n = Math.min(Math.max(1, topN), totalCiCount);
    return sorted.slice(0, n);
  }, [sorted, topN, totalCiCount]);

  const tree = useMemo(
    () =>
      displayedRows.map((r, i) => ({
        treemapKey: `ci-${i}`,
        ciLabel: r.label,
        value: r.count,
      })),
    [displayedRows]
  );

  const treeSignature = useMemo(
    () => displayedRows.map((r, i) => `ci-${i}:${r.label}:${r.count}`).join('|'),
    [displayedRows]
  );

  const topCountControl =
    totalCiCount > 0 ? (
      <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
        <span className="font-medium text-slate-500">Top</span>
        <select
          value={topN}
          onChange={(e) => setTopN(Number(e.target.value))}
          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-200"
          aria-label="Number of top CIs to display"
        >
          {Array.from({ length: totalCiCount }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
    ) : null;

  const headerActions =
    topCountControl || imageDownloadElementId ? (
      <div className="flex items-center gap-2">
        {topCountControl}
        {imageDownloadElementId ? (
          <MsrChartImageDownloadMenu
            elementId={imageDownloadElementId}
            chartTitle={title}
            disabled={imageDownloadDisabled}
          />
        ) : null}
      </div>
    ) : null;

  const bumpTreemapLayout = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.resize();
    chart.update();
  }, []);

  useLayoutEffect(() => {
    if (!sorted.length) return;
    bumpTreemapLayout();
    const raf = requestAnimationFrame(() => bumpTreemapLayout());
    const t1 = window.setTimeout(() => bumpTreemapLayout(), 80);
    const t2 = window.setTimeout(() => bumpTreemapLayout(), 300);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [sorted.length, treeSignature, bumpTreemapLayout]);

  useEffect(() => {
    if (!sorted.length) return;
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => bumpTreemapLayout());
    ro.observe(el);
    return () => ro.disconnect();
  }, [sorted.length, treeSignature, bumpTreemapLayout]);

  useEffect(() => {
    if (!sorted.length) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.05)) {
          bumpTreemapLayout();
        }
      },
      { threshold: [0, 0.05, 0.25] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sorted.length, treeSignature, bumpTreemapLayout]);

  if (!sorted.length) {
    return (
      <KpiChartPanel title={title} className="h-full" flexFill titleClassName="text-center">
        <p className="px-4 py-10 text-center text-sm text-slate-500">{emptyMessage}</p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel
      title={title}
      className="h-full"
      flexFill
      titleClassName=""
      titleEnd={headerActions}
    >
      <div
        ref={containerRef}
        className="relative w-full px-1"
        style={{ height: CHART_HEIGHT_PX, minHeight: CHART_HEIGHT_PX }}
        data-msr-treemap="true"
      >
        <Chart
          key={treeSignature}
          ref={chartRef}
          type={'treemap' as never}
          data={{
            datasets: [
              {
                type: 'treemap',
                label: title,
                data: [],
                tree,
                key: 'value',
                groups: ['treemapKey'],
                spacing: 2,
                borderWidth: 2,
                borderColor: '#ffffff',
                backgroundColor: (ctx: ScriptableContext<'treemap'>) => {
                  if (ctx.type !== 'data') return 'transparent';
                  const i = typeof ctx.dataIndex === 'number' ? ctx.dataIndex : 0;
                  return MSR_TREEMAP_COLORS[i % MSR_TREEMAP_COLORS.length];
                },
                labels: {
                  display: true,
                  color: '#ffffff',
                  position: 'middle',
                  align: 'left',
                  overflow: 'cut',
                  padding: 8,
                  formatter(ctx: ScriptableContext<'treemap'>) {
                    if (ctx.type !== 'data') return '';
                    const raw = ctx.raw as TreemapLeafRaw;
                    const name = ciLabelFromLeafRaw(raw);
                    const short = name.length > 24 ? `${name.slice(0, 22)}…` : name;
                    const v = raw.v ?? 0;
                    return `${short || '—'}, ${v.toLocaleString()}`;
                  },
                  font: { size: 11, weight: 'bold' },
                },
                captions: {
                  display: false,
                },
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: 'bottom',
                labels: {
                  boxWidth: 14,
                  boxHeight: 14,
                  padding: 14,
                  color: '#334155',
                  font: { size: 10 },
                  generateLabels: (chart) => {
                    const ds = chart.data.datasets[0];
                    const data = (ds?.data ?? []) as TreemapLeafRaw[];
                    return data.map((raw, i) => {
                      const name = ciLabelFromLeafRaw(raw);
                      return {
                        text: name || `CI ${i + 1}`,
                        fillStyle: MSR_TREEMAP_COLORS[i % MSR_TREEMAP_COLORS.length],
                        strokeStyle: '#ffffff',
                        lineWidth: 0,
                        hidden: false,
                        index: i,
                      };
                    });
                  },
                },
              },
              tooltip: {
                callbacks: {
                  title: (items) => {
                    const raw = items[0]?.raw as TreemapLeafRaw | undefined;
                    const name = raw ? ciLabelFromLeafRaw(raw) : '';
                    const v = raw?.v ?? 0;
                    return `${name || 'CI'}: ${v.toLocaleString()} tickets`;
                  },
                  label: () => '',
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default MsrCiTreemapChart;
